import DT from '@repo/alt/DependencyTypes'
import { SystemEvents } from '@repo/alt/Services/constants'
import { AppApiGateway } from '@repo/api/web/AppApiGateway'
import type {
  ConfirmChallengeRequest,
  MiniAppAuthRequest,
  MiniAppSelectAccountRequest,
  SignInCompletedResponse,
  SignInRequest,
  SignUpRequest,
} from '@repo/api/web/models'
import { inject, injectable } from 'inversify'
import type { Emitter } from 'nanoevents'

import { AuthTokenStorage } from './Auth.TokenStorage'

import { useAuthStore } from '#Auth/store'
import { useCartStore } from '#Cart/store'
import { usePromotionStore } from '#Promotion/store'
import { UserService } from '#User/services'

@injectable()
export class AuthService {
  private $store: ReturnType<typeof useAuthStore>
  protected cartStore: ReturnType<typeof useCartStore>
  protected promotionStore: ReturnType<typeof usePromotionStore>
  protected appConfig: ReturnType<typeof useAppConfig>

  constructor(
    @inject(AppApiGateway)
    readonly api: AppApiGateway,
    @inject(AuthTokenStorage)
    readonly $tokenStorage: AuthTokenStorage,
    @inject(UserService)
    readonly $userService: UserService,
    @inject(DT.EventBus)
    readonly $eventBus: Emitter,
  ) {
    this.$store = useAuthStore()
    this.cartStore = useCartStore()
    this.promotionStore = usePromotionStore()
    this.appConfig = useAppConfig()
  }

  login(model: SignInRequest) {
    return this.api.authSignIn(this.appConfig.host, model)
  }

  signUp(model: SignUpRequest) {
    return this.api.authSignUp(this.appConfig.host, model)
  }

  confirm(model: ConfirmChallengeRequest) {
    return this.api.authConfirm(this.appConfig.host, model)
  }

  /**
   * Platform launch auth. Body is `{ payload }` only — no `provider`.
   * Channel type on the showcase is the server-side discriminator.
   */
  authMiniApp(payload: string) {
    const model = { payload } as MiniAppAuthRequest
    return this.api.authMiniApp(this.appConfig.host, model)
  }

  /**
   * Telegram multi-link account pick after `accountChoice`.
   */
  authMiniAppSelect(token: string, userId: number) {
    const model = { token, userId } as MiniAppSelectAccountRequest
    return this.api.authMiniAppSelect(this.appConfig.host, model)
  }

  logout() {
    return this.api.authSignOut(this.appConfig.host)
  }

  logoutAll() {
    return this.api.authSignOutAll(this.appConfig.host)
  }

  setToken(token: string) {
    this.$store.setToken(token)
    this.$tokenStorage.token.set(token)
  }

  emitLoggedIn() {
    this.$eventBus.emit(SystemEvents.LOGGED_IN)
  }

  async signIn(token: string) {
    await this.loadSession(token)

    await this.$userService.loadCurrentUser()

    this.emitLoggedIn()
  }

  /**
   * Persist a completed Mini App / select response (memory + localStorage Bearer).
   * Atomic: if loadCurrentUser fails, local Bearer/session are cleared before rethrow
   * so the gate cannot stay blocked with a live Authorization header.
   */
  async applyCompletedSession(info: SignInCompletedResponse) {
    if (!info.token) {
      throw new Error('SignInCompletedResponse has no token')
    }

    try {
      this.setToken(info.token)
      this.$store.setSession(info)
      await this.$userService.loadCurrentUser()
      this.emitLoggedIn()
    }
    catch (error) {
      this.resetLocalClientState()
      throw error
    }
  }

  getToken() {
    return this.$tokenStorage.token.get() || this.$store.token
  }

  checkSession() {
    return this.api.authCheckSession(this.appConfig.host)
  }

  async loadSession(token: string) {
    this.setToken(token)
    const info = await this.checkSession()
    this.$store.setSession(info)
  }

  getConfig() {
    return this.api.authAuthConfig(this.appConfig.host)
  }

  resetStore() {
    this.$store.reset()
  }

  resetTokenStorage() {
    this.$tokenStorage.token.set(null)
  }

  /**
   * Clears local auth/user/cart/promo client state without calling logout API.
   * Used by the Mini App platform gate to drop leftover website session material.
   */
  resetLocalClientState() {
    this.resetStore()
    this.resetTokenStorage()
    this.$userService.resetStore()
    this.cartStore.reset()
    this.promotionStore.resetPricing()
  }

  signInAnonim() {
    return this.api.authSignInAnonym(this.appConfig.host)
  }

  /**
   * @deprecated Mini App has no guest session. Do not call for storefront entry.
   */
  async createAnonimSession() {
    throw new Error('Anonymous session is not available in Mini App')
  }

  signOut() {
    this.resetLocalClientState()
    this.$eventBus.emit(SystemEvents.LOGGED_OUT)
    this.api.authSignOut(this.appConfig.host)
  }
}
